<!--@format-->
<template>
    <div class="proof-of-payment-widget">
        <div class="widget-container">
            <div class="widget-header">
                <h3>{{ title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div class="payment-details">
                        <invoice-details :details="payment.detailsData" />
                    </div>
                    <div class="proof-file">
                        <div v-if="isFileUploadVisible">
                            <div v-if="payment.proof.hasProof">
                                <label class="proof-file-label">Uploaded Document</label>
                                <app-files-preview :files="payment.files">
                                    <template slot="actions">
                                        <app-icon-button
                                            class="download-action"
                                            @click="handleDownloadProof"
                                        >
                                            <e6-download-cloud-svg color="#f30070" />
                                        </app-icon-button>
                                    </template>
                                </app-files-preview>
                            </div>
                            <div v-else>
                                <label class="proof-file-label">No proof uploaded</label>
                                <app-button
                                    v-if="!payment.proof.proofRequested"
                                    class="request-proof"
                                    @click="handleRequestProof"
                                >
                                    Request Proof
                                </app-button>

                                <div v-else class="requested-proof">Proof Requested</div>
                            </div>
                        </div>
                        <app-button
                            :loading="loadingDownload"
                            class="print-btn"
                            @click="handlePrintDetails"
                        >
                            Print <k26-print-outline-svg />
                        </app-button>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <app-button class="back-btn" @click="closeWidget"> Back </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { ClientPaymentsReceivedService } from '@/scripts/services/mdf/client/client-payments-received-service';
import { toLower } from 'lodash';
import { MdfInvoiceMixin } from '@/mixins/mdf-invoice-mixin';

const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const AppButton = () => import('@/components/common/app-button.vue');
const InvoiceDetails = () => import('@/components/invoice-template/invoice-details');
const AppFilesPreview = () => import('@/components/common/app-files-preview');
const AppIconButton = () => import('@/components/common/app-icon-button');
const E6DownloadCloudSvg = () => import('@/components/svg/grid/e6-download-cloud-svg');
const K26PrintOutlinedSvg = () => import('@/components/svg/grid/k26-print-outline-svg');

export default {
    name: 'proof-of-payment-widget',

    components: {
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'app-button': AppButton,
        'invoice-details': InvoiceDetails,
        'app-files-preview': AppFilesPreview,
        'app-icon-button': AppIconButton,
        'e6-download-cloud-svg': E6DownloadCloudSvg,
        'k26-print-outline-svg': K26PrintOutlinedSvg,
    },

    mixins: [MdfInvoiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            loadingDownload: false,
            payment: {
                paymentMethod: '',
                detailsData: [],
                files: [],
                proof: {
                    hasProof: false,
                    proofRequested: false,
                },
            },

            invoiceDetails: {},
        };
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        invoiceId() {
            return this.widget.options.invoiceId;
        },

        title() {
            return this.widget.options.title;
        },

        isFileUploadVisible() {
            return toLower(this.payment.paymentMethod) === 'marked as paid';
        },
    },

    created() {
        this.getPaymentData();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handlePrintDetails() {
            this.loadingDownload = true;

            this.printMdfInvoicePdf(this.invoiceDetails).then(() => {
                this.loadingDownload = false;
            });
        },

        getPaymentData() {
            this.isLoading = true;

            ClientPaymentsReceivedService.getProof(this.accountId, this.invoiceId)
                .then(({ data: { data } }) => {
                    const proof =
                        data.Proof && data.Proof.ProofUrl
                            ? [
                                  {
                                      name: data.Proof.FileName,
                                      file: data.Proof.ProofUrl,
                                      size: data.Proof.Size,
                                      type: data.Proof.MimeType,
                                      downloadUrl: data.Proof.ProofUrl,
                                  },
                              ]
                            : false;

                    this.payment = {
                        paymentMethod: data.PaymentMethod,
                        detailsData: [
                            [
                                {
                                    title: 'Received From',
                                    content: data.ReceivedFrom,
                                },
                                {
                                    title: 'Invoice Number',
                                    content: data.InvoiceNumber,
                                },
                                {
                                    title: 'Transaction Date',
                                    content:
                                        data.TransactionDate &&
                                        this.$moment(data.TransactionDate)
                                            .local()
                                            .format('MMM DD, YYYY')
                                            .toUpperCase(),
                                },
                                {
                                    title: 'Payment Type',
                                    content: data.PaymentType,
                                },
                                {
                                    title: 'Payment Method',
                                    content: data.PaymentMethod,
                                },
                            ],
                            [
                                {
                                    title: 'Total:',
                                    content: this.$helpers.formatCurrency(data.Total),
                                    contentClass: 'total-amount',
                                },
                            ],
                        ],

                        files: proof,
                        proof: {
                            hasProof: data.Proof && data.Proof.ProofUrl,
                            proofRequested: data.Proof && !data.Proof.ProofUrl,
                        },
                    };

                    const billedTo = this.$store.state.context.account.name;

                    this.invoiceDetails = {
                        invoiceNumber: data.InvoiceNumber,
                        paymentMethod: data.PaymentMethod,
                        proofOfPayment: proof,
                        address: billedTo,
                        paymentDate:
                            data.TransactionDate && this.$moment.utc(data.TransactionDate).local(),
                        companyName: billedTo,
                        items: [data.PaymentType],
                        amount: data.Total,
                        baseFee: data.Total,
                        isMarkedAsPaid: data.PaymentMethod === 'Marked as Paid',
                        isPaid: true,
                        fundTitle: data.FundTitle,
                    };
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to get your payment data.', {
                        error,
                    });

                    this.closeWidget();
                })
                .finally(() => (this.isLoading = false));
        },

        handleDownloadProof() {
            window.open(this.payment.files[0].file, '_blank');
        },

        handleRequestProof() {
            const data = {
                invoiceId: this.invoiceId,
            };

            EventBus.$emit('request-proof', data);
        },
    },
};
</script>

<style>
.proof-of-payment-widget .widget-container {
    width: 100%;
    height: 100%;
}

.proof-of-payment-widget .widget-container .content-wrapper {
    padding: 23px 29px 23px 33px;
}

.proof-of-payment-widget .content-wrapper .loading-screen {
    text-align: center;
}

.proof-of-payment-widget .proof-file {
    margin-top: 20px;
    display: flex;
}

.proof-of-payment-widget .app-icon-button {
    padding: 0 10px;
}

.proof-of-payment-widget .app-icon-button svg {
    width: 30px;
}

.proof-of-payment-widget .proof-file .print-btn {
    display: flex;
    gap: 19px;
    margin-left: auto;
}

.proof-of-payment-widget .proof-file .print-btn .k26-print-outline-svg path {
    stroke: #ffffff;
}

.proof-of-payment-widget .request-proof {
    background-color: #ffffff;
    color: #18aeff;
    border: solid 1px #18aeff;
    width: 160px;
    margin-top: 8px;
}

.proof-of-payment-widget .request-proof:hover {
    background-color: #18aeff;
    color: #ffffff;
}

.proof-of-payment-widget .requested-proof {
    display: flex;
    align-items: center;
    font-size: 14px;
    height: 40px;
    margin-top: 8px;
    padding: 6px 25px;
    background-color: #fbe5ce;
    color: #eb7e0a;
    border-radius: 22px;
    font-weight: 500;
    width: 160px;
}

.proof-of-payment-widget .widget-container .widget-footer {
    justify-content: flex-end;
    padding: 0 70px 0 35px;
}

.proof-of-payment-widget .back-btn {
    width: 170px;
}
</style>
